import { CloseCircleOutlined } from '@ant-design/icons';
import { CodeNode } from '@lexical/code';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { TRANSFORMERS } from '@lexical/markdown';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { App } from 'antd';
import { $getRoot } from 'lexical';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

import { ExcalidrawNode } from './nodes/ExcalidrawNode';
import { ImageNode } from './nodes/ImageNode';
import { KeywordNode } from './nodes/KeywordNode';
import { MentionNode } from './nodes/MentionNode';
import { YouTubeNode } from './nodes/YouTubeNode';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import ExcalidrawPlugin from './plugins/ExcalidrawPlugin';
import FloatingLinkPreviewPlugin from './plugins/FloatingLinkPreviewPlugin';
import ImagePlugin from './plugins/ImagePlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import TablePlugin from './plugins/TablePlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import YouTubePlugin from './plugins/YouTubePlugin';
import defaultEmailLexicalCSS from './themes/emailStyles';
import ExampleTheme from './themes/ExampleTheme';

const editorConfig = {
  theme: ExampleTheme,
  onError(error: Error) {
    throw error;
  },
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
    ImageNode,
    YouTubeNode,
    MentionNode,
    KeywordNode,
    ExcalidrawNode,
  ],
};

interface RichLexicalEditorProps {
  value?: string;
  placeholder?: string;
  setValue: (value: string) => void;
  className?: string;
  // When true, prepend CSS (style tag) to the emitted HTML value
  includeCssInValue?: boolean;
  // Optional CSS string to prepend. If omitted, a minimal default is used
  cssString?: string;
  // When true, wrap output as full HTML document (doctype/head/body)
  wrapAsEmailHtml?: boolean;
}

function DefaultValuePlugin({ defaultValue }: { defaultValue: string }) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [editor] = useLexicalComposerContext();

  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  if (defaultValue && !isInitialized) {
    editor.update(() => {
      if (editor?.getEditorState()?.isEmpty()) {
        try {
          const parser = new DOMParser();
          const dom = parser.parseFromString(defaultValue, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();
          nodes.forEach((n) => root.append(n));
          setIsInitialized(true);
        } catch (error) {
          notification.error({
            message: t('error'),
            description: 'Invalid text format detected',
            icon: (
              <span className="text-red-600">
                <CloseCircleOutlined />
              </span>
            ),
          });
        }
      }
    });
  }

  return null;
}

export function RichLexicalEditor({
  value,
  placeholder,
  setValue,
  className,
  includeCssInValue,
  cssString,
  wrapAsEmailHtml,
}: RichLexicalEditorProps) {
  const cssProvidedOrDefault = cssString ?? defaultEmailLexicalCSS;
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  return (
    <LexicalComposer
      initialConfig={{
        ...editorConfig,
        editable: true,
        namespace: 'Editor',
        editorState: null,
      }}
    >
      <div className="editor-container">
        <ToolbarPlugin />
        <div className={`editor-inner editor-shell ${className}`}>
          <RichTextPlugin
            contentEditable={
              <div className="editor-scroller">
                <div className="editor" ref={onRef}>
                  <ContentEditable className="editor-input" />
                </div>
              </div>
            }
            placeholder={
              <div className="editor-placeholder">
                {placeholder || 'Begin writing your text...'}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {value && <DefaultValuePlugin defaultValue={value} />}
          <OnChangePlugin
            onChange={(editorState, editor) => {
              editor.update(() => {
                const html = $generateHtmlFromNodes(editor, null);
                if (includeCssInValue) {
                  const styleTag = cssProvidedOrDefault
                    ? `<style>${cssProvidedOrDefault}</style>`
                    : '';
                  const fullHtml = wrapAsEmailHtml
                    ? `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>${styleTag}</head><body>${html}</body></html>`
                    : `${styleTag}${html}`;
                  setValue(fullHtml);
                } else {
                  setValue(html);
                }
              });
            }}
          />
          <ImagePlugin captionsEnabled={false} />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <AutoLinkPlugin />
          <YouTubePlugin />
          {floatingAnchorElem && (
            <FloatingLinkPreviewPlugin anchorElem={floatingAnchorElem} />
          )}
          <TablePlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          {floatingAnchorElem && (
            <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
          )}
          <ExcalidrawPlugin />
        </div>
      </div>
    </LexicalComposer>
  );
}

export default RichLexicalEditor;
